import React, { useState, useEffect } from 'react';
import { Edit, Trash2, Plus, Search, FileText, Mail, Phone, Building } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { clientService } from '../../services/clientService';
import { Customer } from '../../types/customer';

interface CustomerListProps {
  onEdit: (customer: Customer) => void;
  onAdd: () => void;
  onGenerateQuote: (customer: Customer) => void;
}

const CustomerList = ({ onEdit, onAdd, onGenerateQuote }: CustomerListProps) => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  const statusOptions = [
    { id: 'all', name: 'Todos', color: 'bg-gray-100 text-gray-800' },
    { id: 'active', name: 'Activo', color: 'bg-green-100 text-green-800' },
    { id: 'inactive', name: 'Inactivo', color: 'bg-red-100 text-red-800' },
    { id: 'prospect', name: 'Prospecto', color: 'bg-yellow-100 text-yellow-800' }
  ];

  useEffect(() => {
    loadCustomers();
  }, []);

  const loadCustomers = async () => {
    const allCustomers = await clientService.getCustomers();
    setCustomers(allCustomers);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('¿Está seguro de que desea eliminar este cliente? Esta acción no se puede deshacer.')) {
      const success = await clientService.deleteCustomer(id);
      if (success) {
        loadCustomers();
      }
    }
  };

  const handleStatusChange = async (id: number, newStatus: Customer['status']) => {
    const success = await clientService.updateCustomer(id, { status: newStatus });
    if (success) {
      loadCustomers();
    }
  };

  const filteredCustomers = customers.filter(customer => {
    const matchesSearch = 
      customer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customer.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.company && customer.company.toLowerCase().includes(searchTerm.toLowerCase())) ||
      (customer.ruc && customer.ruc.toLowerCase().includes(searchTerm.toLowerCase()));
    
    const matchesStatus = filterStatus === 'all' || customer.status === filterStatus;
    
    return matchesSearch && matchesStatus;
  });

  const getStatusColor = (status: Customer['status']) => {
    const statusOption = statusOptions.find(s => s.id === status);
    return statusOption ? statusOption.color : 'bg-gray-100 text-gray-800';
  };

  const getStatusName = (status: Customer['status']) => {
    const statusOption = statusOptions.find(s => s.id === status);
    return statusOption ? statusOption.name : status;
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-gray-900">Gestión de Clientes</h2>
        <Button onClick={onAdd} className="btn-primary">
          <Plus className="w-4 h-4 mr-2" />
          Agregar Cliente
        </Button>
      </div>

      {/* Filters */}
      <div className="bg-white p-4 sm:p-6 rounded-lg shadow-sm border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <input
              type="text"
              placeholder="Buscar clientes..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
          
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          >
            {statusOptions.map(status => (
              <option key={status.id} value={status.id}>
                {status.name}
              </option>
            ))}
          </select>

          <div className="text-sm text-gray-500 flex items-center">
            Total: {filteredCustomers.length} clientes
          </div>
        </div>
      </div>

      {/* Customers Table */}
      <div className="bg-white rounded-lg shadow-sm border overflow-hidden">
        {/* Vista de tabla para pantallas grandes */}
        <div className="hidden lg:block">
          <table className="w-full table-fixed">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '25%'}}>
                  Cliente
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '20%'}}>
                  Empresa
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '15%'}}>
                  Contacto
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                  RUC
                </th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '10%'}}>
                  Estado
                </th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider" style={{width: '20%'}}>
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {filteredCustomers.map((customer) => (
                <tr key={customer.id} className="hover:bg-gray-50">
                  <td className="px-4 py-4">
                    <div className="flex items-center">
                      <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center mr-3 flex-shrink-0">
                        <span className="text-primary-600 font-medium text-sm">
                          {customer.name.charAt(0).toUpperCase()}
                        </span>
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate" title={customer.name}>
                          {customer.name}
                        </div>
                        <div className="text-sm text-gray-500 truncate flex items-center">
                          <Mail className="w-3 h-3 mr-1" />
                          {customer.email}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 truncate" title={customer.company}>
                      {customer.company || '-'}
                    </div>
                    <div className="text-sm text-gray-500 truncate flex items-center">
                      <Building className="w-3 h-3 mr-1" />
                      {customer.city || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-4">
                    <div className="text-sm text-gray-900 truncate flex items-center">
                      <Phone className="w-3 h-3 mr-1" />
                      {customer.phone || '-'}
                    </div>
                  </td>
                  <td className="px-4 py-4 text-sm text-gray-900 truncate">
                    {customer.ruc || '-'}
                  </td>
                  <td className="px-4 py-4">
                    <select
                      value={customer.status || 'active'}
                      onChange={(e) => handleStatusChange(customer.id!, e.target.value as Customer['status'])}
                      className={`px-2 py-1 text-xs font-medium rounded-full border-0 cursor-pointer ${getStatusColor(customer.status || 'active')}`}
                    >
                      <option value="active">Activo</option>
                      <option value="inactive">Inactivo</option>
                      <option value="prospect">Prospecto</option>
                    </select>
                  </td>
                  <td className="px-4 py-4 text-sm font-medium">
                    <div className="flex justify-center space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onGenerateQuote(customer)}
                        className="flex-shrink-0"
                        title="Generar Cotización"
                      >
                        <FileText className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(customer)}
                        className="flex-shrink-0"
                        title="Editar"
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(customer.id!)}
                        className="text-red-600 hover:text-red-800 hover:border-red-300 flex-shrink-0"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Vista de tarjetas para pantallas pequeñas */}
        <div className="lg:hidden">
          <div className="divide-y divide-gray-200">
            {filteredCustomers.map((customer) => (
              <div key={customer.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start space-x-3">
                  <div className="w-12 h-12 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-primary-600 font-medium">
                      {customer.name.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {customer.name}
                    </h3>
                    <div className="text-sm text-gray-500 mt-1 space-y-1">
                      <div className="flex items-center">
                        <Mail className="w-3 h-3 mr-1" />
                        {customer.email}
                      </div>
                      {customer.company && (
                        <div className="flex items-center">
                          <Building className="w-3 h-3 mr-1" />
                          {customer.company}
                        </div>
                      )}
                      {customer.phone && (
                        <div className="flex items-center">
                          <Phone className="w-3 h-3 mr-1" />
                          {customer.phone}
                        </div>
                      )}
                    </div>
                    
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      <select
                        value={customer.status || 'active'}
                        onChange={(e) => handleStatusChange(customer.id!, e.target.value as Customer['status'])}
                        className={`px-2 py-1 text-xs font-medium rounded-full border-0 cursor-pointer ${getStatusColor(customer.status || 'active')}`}
                      >
                        <option value="active">Activo</option>
                        <option value="inactive">Inactivo</option>
                        <option value="prospect">Prospecto</option>
                      </select>
                      
                      {customer.ruc && (
                        <span className="text-xs text-gray-500">
                          RUC: {customer.ruc}
                        </span>
                      )}
                    </div>

                    <div className="mt-4 flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onGenerateQuote(customer)}
                        className="flex-1"
                      >
                        <FileText className="w-4 h-4 mr-1" />
                        Cotizar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onEdit(customer)}
                        className="flex-1"
                      >
                        <Edit className="w-4 h-4 mr-1" />
                        Editar
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDelete(customer.id!)}
                        className="text-red-600 hover:text-red-800 hover:border-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
        
        {filteredCustomers.length === 0 && (
          <div className="text-center py-12">
            <div className="text-gray-500">No se encontraron clientes</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CustomerList;